import React from 'react';
import { SEO } from '@/components/SEO';
import ServiceSchema from '@/components/services/ServiceSchema';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ServiceHero from '@/components/services/ServiceHero';
import ServiceBenefits from '@/components/services/ServiceBenefits';
import ServiceFAQ from '@/components/services/ServiceFAQ';
import ServiceCTA from '@/components/services/ServiceCTA';
import ServiceCrossLinks from '@/components/services/ServiceCrossLinks';
import { Trash2 } from 'lucide-react';
import dumpsterBefore1 from '@/assets/dumpster-pad-before-1.jpg';
import dumpsterAfter1 from '@/assets/dumpster-pad-after-1.jpg';
import dumpsterBefore2 from '@/assets/dumpster-pad-before-2.jpg';
import dumpsterAfter2 from '@/assets/dumpster-pad-after-2.jpg';
import dumpsterProcess from '@/assets/dumpster-pad-process.jpg';

const DumpsterPadCleaningPage = () => {
  const serviceData = {
    title: "Dumpster & Dumpster Pad Cleaning in Ventura, CA",
    subtitle: "Degreasing and Sanitizing for Restaurants, HOAs, and Commercial Properties",
    description: "Eliminate grease, grime, and odor-causing bacteria from dumpster enclosures and pads. Our hot water pressure washing and degreasing service removes buildup that attracts pests and violates health and property codes, with all wastewater safely contained and disposed of.",
    icon: <Trash2 className="h-16 w-16 text-ventura-blue" />,
    beforeImage: dumpsterBefore1,
    afterImage: dumpsterAfter1
  };

  const benefits = [
    "Eliminates grease, grime, and odor-causing bacteria",
    "Deters flies, rodents, and other pests",
    "Helps meet health code and property management standards",
    "Removes staining from concrete dumpster pads",
    "Contained wastewater recovery for compliant, eco-friendly disposal",
    "Licensed and insured commercial service"
  ];

  const faqs = [
    {
      question: "How often should dumpster pads be cleaned?",
      answer: "Restaurants and food service properties should have dumpster enclosures cleaned monthly to control grease buildup and odor. Retail centers, apartment complexes, and HOAs typically do well with quarterly cleaning, though high-traffic areas may need more frequent service."
    },
    {
      question: "Where does the wastewater go?",
      answer: "We use professional containment and recovery equipment to capture wastewater during the cleaning process rather than letting it run into storm drains, keeping the service compliant with local water quality regulations."
    },
    {
      question: "Do you clean the dumpster itself or just the concrete pad?",
      answer: "Both. We degrease and pressure wash the interior and exterior of the dumpster along with the surrounding concrete pad and enclosure walls, removing grease stains, grime, and buildup from all surfaces."
    },
    {
      question: "Do you offer recurring service for businesses and HOAs?",
      answer: "Yes. Many of our commercial clients throughout Ventura County put dumpster pad cleaning on a recurring monthly or quarterly schedule so enclosures stay compliant and odor-free year-round."
    }
  ];

  return (
    <>
      <SEO
        title="Dumpster Pad Cleaning Ventura CA | Commercial Degreasing Services"
        description="Professional dumpster and dumpster pad cleaning in Ventura, CA. Degreasing for restaurants, HOAs, and commercial properties with contained wastewater disposal. Free estimates!"
        keywords="dumpster pad cleaning Ventura CA, dumpster enclosure cleaning Camarillo, commercial degreasing Thousand Oaks, trash corral cleaning Ventura County"
        path="/services/dumpster-pad-cleaning"
      />
      <ServiceSchema
        serviceName="Dumpster & Dumpster Pad Cleaning"
        description="Professional dumpster and dumpster pad cleaning in Ventura, CA. Degreasing for restaurants, HOAs, and commercial properties with contained wastewater disposal."
        serviceType="Dumpster Pad Cleaning"
        areaServed={["Ventura", "Oxnard", "Camarillo", "Thousand Oaks", "Simi Valley", "Fillmore", "Moorpark", "Port Hueneme", "Santa Paula"]}
        additionalServices={["Pressure Washing", "Soft Wash"]}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <ServiceHero {...serviceData} />

        <section className="section-padding bg-white">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Why Dumpster Pads Need Professional Degreasing</h2>
              <p className="text-lg text-gray-600 mb-8 text-center">
                Dumpster enclosures accumulate grease, food waste residue, and organic buildup that ordinary hosing can't remove.
                Left untreated, this buildup causes persistent odor, attracts pests, and stains concrete pads permanently. Professional
                hot water pressure washing breaks down grease at the source, keeping enclosures sanitary and compliant.
              </p>

              <div className="max-w-4xl mx-auto mb-12">
                <h3 className="text-3xl font-bold mb-6 text-center">Dumpster Enclosure Cleaning Ventura CA</h3>
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-gray-600 mb-4">
                      We use hot water pressure washing and commercial-grade degreasers to strip away built-up grease and grime from
                      the interior walls of the dumpster, the enclosure, and the surrounding concrete pad. Hot water cuts through
                      grease that cold water alone can't touch, which is essential for restaurants and food service properties where
                      grease accumulation is constant.
                    </p>
                    <p className="text-gray-600 mb-4">
                      All wastewater is captured and contained during the cleaning process rather than discharged into storm drains,
                      keeping the service compliant with Ventura County water quality regulations.
                    </p>
                    <div className="bg-primary/10 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-primary mb-2">Dumpster Cleaning from $150–$250 Per Bin</h4>
                      <p className="text-sm text-gray-600">Pad cleaning starts around $100. Final pricing depends on how much oil, grease, and buildup we're dealing with.</p>
                    </div>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">FAQ: Is dumpster pad cleaning required by code?</h4>
                      <p className="text-gray-600 text-sm">
                        Many cities and property management agreements in Ventura County require food service businesses to keep
                        dumpster enclosures clean and odor-free. Regular professional cleaning helps properties stay compliant with
                        health department and lease requirements.
                      </p>
                    </div>
                  </div>
                  <div className="before-after-container">
                    <div className="before-after-image">
                      <img
                        src={dumpsterBefore2}
                        alt="Grease-stained dumpster interior floor before professional degreasing in Ventura County"
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <span className="before-after-label before-label">Before</span>
                    </div>
                    <div className="before-after-image">
                      <img
                        src={dumpsterAfter2}
                        alt="Clean dumpster exterior after professional pressure washing in Ventura CA"
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <span className="before-after-label after-label">After</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Complete Dumpster Area Cleaning</h3>
                  <p className="text-gray-600 mb-4">
                    Our dumpster and pad cleaning service covers every part of the enclosure:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Dumpster interior and exterior surfaces</li>
                    <li>Concrete pad and drainage areas</li>
                    <li>Enclosure walls and gates</li>
                    <li>Grease and stain removal</li>
                    <li>Odor treatment</li>
                    <li>Contained wastewater recovery and disposal</li>
                  </ul>
                </div>
                <div>
                  <img
                    src={dumpsterProcess}
                    alt="Dumpster interior being degreased with cleaning solution during professional pad cleaning service"
                    className="w-full h-72 object-cover rounded-lg shadow-lg"
                  />
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Built for Restaurants, HOAs, and Property Managers</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Restaurants & Food Service</h4>
                    <p className="text-gray-600 mb-4">
                      Grease trap overflow and food waste residue make restaurant dumpster pads some of the most demanding cleaning
                      jobs. We recommend monthly service to keep odor and pest issues from becoming a health code problem.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">HOAs & Apartment Communities</h4>
                    <p className="text-gray-600 mb-4">
                      Shared trash corrals and dumpster enclosures in multi-family communities across Ventura County take a beating
                      from daily use. Quarterly cleaning keeps common areas presentable and resident complaints down.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-2xl font-semibold mb-4">Eco-Friendly, Compliant Cleaning</h3>
                <p className="text-gray-600">
                  As a locally-owned Ventura County business, we contain and properly dispose of all wastewater generated during
                  dumpster and pad cleaning rather than letting it run into storm drains. This keeps our clients compliant with
                  local water quality regulations while still delivering a thorough, grease-cutting clean.
                </p>
              </div>
            </div>
          </div>
        </section>

        <ServiceBenefits benefits={benefits} />
        <ServiceCrossLinks
          currentService="Dumpster Pad Cleaning"
          relatedServices={[
            { name: 'Pressure Washing', path: '/services/pressure-washing', icon: 'pressure' },
            { name: 'Soft Wash', path: '/services/soft-wash', icon: 'soft' },
            { name: 'Gutter Cleaning', path: '/services/gutter-cleaning', icon: 'gutter' },
          ]}
        />
        <ServiceFAQ faqs={faqs} serviceName="Dumpster Pad Cleaning" />
        <ServiceCTA
          title="Ready for a Clean, Odor-Free Dumpster Area?"
          description="Get your free dumpster and dumpster pad cleaning estimate today for your restaurant, HOA, or commercial property."
        />
        <Footer />
      </div>
    </>
  );
};

export default DumpsterPadCleaningPage;
